import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { getToken, getCachedUser, saveSession, clearSession } from "@/lib/authStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    // Ripristino ottimistico: mostra subito l'utente in cache (niente flash di login)
    const cached = getCachedUser();
    if (cached) setUser(cached);
    let attempt = 0;
    while (true) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        saveSession(null, data); // aggiorna la cache nella stessa posizione (local/session)
        break;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Token realmente scaduto/invalido/revocato -> login
          clearSession();
          setUser(false);
          break;
        }
        // Errore di rete/timeout/5xx: NON cancellare il token persistente, ritenta
        attempt += 1;
        if (attempt >= 3) {
          if (!cached) setUser(false); // nessun dato per il ripristino: mostra login (token conservato)
          break;
        }
        await new Promise((r) => setTimeout(r, 700 * attempt));
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (token, userData, remember = false) => {
    saveSession(token, userData, remember);
    setUser(userData);
  };

  const switchProfile = async (target) => {
    const { data } = await api.post("/auth/switch-profile", { target });
    saveSession(data.token, data.user); // preserva la posizione di storage attuale
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    clearSession();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, switchProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
